'use client';

import { useEffect, useState, createContext, useContext, ReactNode } from 'react';
import { awsConfig } from '@/lib/config/amplify-config';

// Define our user types (no unused types)

// Generic user type that can work with any version of Amplify
interface UserAttributes {
  email?: string;
  name?: string;
  [key: string]: unknown;
}

// This is a more flexible type that can accommodate Amplify's actual user types
interface AuthUser {
  username?: string;
  userId?: string;
  attributes?: UserAttributes;
  [key: string]: unknown;
}

// Types
type AuthContextType = {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  signIn: (username: string, password: string) => Promise<unknown>;
  signOut: () => Promise<void>;
  resetLoadingState: () => void;
};

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  signIn: async () => null,
  signOut: async () => {},
  resetLoadingState: () => {},
});

// Provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isClient, setIsClient] = useState<boolean>(false);

  // Set isClient to true when component mounts to avoid hydration mismatch
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Initialize Amplify only on the client side
  useEffect(() => {
    if (!isClient) return;

    const initializeAmplify = async () => {
      try {
        // Import Amplify with the newer V6 import structure
        const { Amplify } = await import('aws-amplify');
        
        // Configure Amplify with proper typing for v6
        Amplify.configure({
          Auth: {
            Cognito: {
              userPoolId: awsConfig.Auth.userPoolId,
              userPoolClientId: awsConfig.Auth.userPoolWebClientId,
              loginWith: {
                username: true,
                email: true,
              }
            }
          }
        });
        
        checkAuth();
      } catch (error) {
        console.error('Error initializing Amplify:', error);
        setIsLoading(false);
      }
    };
    
    initializeAmplify();
  }, [isClient]);

  const checkAuth = async () => {
    try {
      // For Auth, we need to use the newer import structure
      const { getCurrentUser } = await import('@aws-amplify/auth');
      const currentUser = await getCurrentUser();
      // AuthUser type is compatible with whatever AWS returns
      setUser(currentUser as AuthUser);
      setIsAuthenticated(true);
    } catch {
      // Ignore the specific error since we just need to know auth failed
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = async (username: string, password: string) => {
    setIsLoading(true);
    try {
      // Import the necessary functions from aws-amplify/auth
      const { signIn: amplifySignIn, signOut: amplifySignOut, getCurrentUser } = await import('@aws-amplify/auth');
      
      // First, check if a user is already signed in
      try {
        await getCurrentUser();
        // If we get here, a user is already signed in - try to sign them out first
        console.log('Detected existing sign-in session, signing out first...');
        await amplifySignOut({ global: true });
      } catch {
        // Ignore the error since no user being signed in is the expected case
        // No user is signed in (this is the expected path)
        console.log('No existing user session detected');
      }
      
      // Now proceed with sign in
      const { isSignedIn, nextStep } = await amplifySignIn({
        username,
        password,
      });
      
      if (isSignedIn) {
        // Get the current user after successful sign-in
        const currentUser = await getCurrentUser();
        setUser(currentUser as AuthUser);
        setIsAuthenticated(true);
        
        // Set a cookie for middleware authentication check
        document.cookie = 'amplify-authenticator-authState=authenticated; path=/; max-age=86400';
        
        return currentUser as unknown;
      } else {
        // Handle multi-factor authentication or other steps if needed
        console.log('Authentication requires additional steps:', nextStep);
        // Here you would implement handling for MFA, etc.
        throw new Error(`Additional authentication steps required: ${
          nextStep?.signInStep || 'unknown step'
        }`);
      }
    } catch (error: unknown) {
      console.error('Error signing in:', error);
      
      // If there's a UserAlreadyAuthenticatedException, try to recover by forcing signOut and retrying
      const errorObj = error as { name?: string; message?: string };
      if (errorObj.name === 'UserAlreadyAuthenticatedException' || 
          (typeof errorObj.message === 'string' && errorObj.message.includes('already a signed in user'))) {
        try {
          console.log('UserAlreadyAuthenticatedException detected, attempting recovery...');
          
          // Force a clean signout
          const { signOut: amplifySignOut } = await import('@aws-amplify/auth');
          await amplifySignOut({ global: true });
          
          // Clear any auth-related storage
          try {
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && (key.includes('amplify') || key.includes('Cognito'))) {
                keysToRemove.push(key);
              }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
          } catch (e) {
            console.error('Error clearing local storage during recovery', e);
          }
          
          // Wait a moment to ensure cleanup is complete
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Retry sign in once
          console.log('Retrying sign in after cleanup...');
          const { signIn: retrySignIn } = await import('@aws-amplify/auth');
          const retryResult = await retrySignIn({
            username,
            password,
          });
          
          if (retryResult.isSignedIn) {
            // Get the current user after successful retry
            const { getCurrentUser } = await import('@aws-amplify/auth');
            const currentUser = await getCurrentUser();
            setUser(currentUser as AuthUser);
            setIsAuthenticated(true);
            
            // Set cookie
            document.cookie = 'amplify-authenticator-authState=authenticated; path=/; max-age=86400';
            
            return currentUser as unknown;
          }
        } catch (retryError) {
          console.error('Error during sign-in recovery attempt:', retryError);
          throw new Error('Failed to sign in even after cleanup. Please try again or refresh the page.');
        }
      }
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      // Don't set isLoading to true to avoid loading state in UI
      
      // Update local state first
      setUser(null);
      setIsAuthenticated(false);
      
      // Clear the auth cookie
      document.cookie = 'amplify-authenticator-authState=; path=/; max-age=0; domain=' + window.location.hostname;
      
      // Clear amplify storage to prevent cached credentials
      try {
        // Use a more thorough cleaning approach for localStorage
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (
            key.includes('amplify') || 
            key.includes('Cognito') || 
            key.includes('CognitoIdentityServiceProvider') ||
            key.includes('aws') || 
            key.includes('AWSCognito')
          )) {
            keysToRemove.push(key);
          }
        }
        
        // Remove all identified keys
        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
        });
        
        // Also clear session storage
        const sessionKeysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && (
            key.includes('amplify') || 
            key.includes('Cognito') || 
            key.includes('CognitoIdentityServiceProvider') ||
            key.includes('aws') || 
            key.includes('AWSCognito')
          )) {
            sessionKeysToRemove.push(key);
          }
        }
        
        sessionKeysToRemove.forEach(key => {
          sessionStorage.removeItem(key);
        });
      } catch (e) {
        console.log('Error clearing storage:', e);
      }
      
      // Now fully sign out from AWS Amplify
      try {
        // Force reload the Amplify Auth module to ensure we get a fresh instance
        const { signOut: amplifySignOut } = await import('@aws-amplify/auth');
        
        // Use global: true to sign out from all devices
        await amplifySignOut({ global: true });
        
        // Clear cookies manually since clearCookies may not be available
        document.cookie.split(';').forEach(cookie => {
          const [name] = cookie.trim().split('=');
          if (name && (
            name.includes('amplify') || 
            name.includes('aws') || 
            name.includes('cognito') ||
            name.includes('id_token') || 
            name.includes('access_token')
          )) {
            // Clear for root path and subdirectories
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
            // Also try with domain
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname};`;
          }
        });
      } catch (amplifyError) {
        console.error('Amplify signOut error:', amplifyError);
        // Continue even if there's an Amplify error - we've already cleared local state
      }
    } catch (error) {
      console.error('General error signing out:', error);
      // We already updated state at the beginning, so we don't need to do it again
      throw error; // Re-throw to let caller handle it
    }
  };

  // Function to explicitly reset loading state
  const resetLoadingState = () => {
    setIsLoading(false);
  };
  
  const value = {
    isAuthenticated,
    isLoading,
    user,
    signIn,
    signOut,
    resetLoadingState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to use auth
export function useAuth() {
  const context = useContext(AuthContext);
  return context;
}